import { faDigits } from '../../lib/numerals'

// ─── Arcade stage helpers (V7-p) ──────────────────────────────────────────────
//
// Pure functions, kept out of StagesTab.jsx so that file only exports
// components (react-refresh) and so the parts that can be wrong without any
// render showing it are unit-testable on their own: what the up/down buttons
// actually send, which lessons the path silently drops, and what a 422 from
// `admin-arcade_*` reads like to the admin.

/**
 * The item at `index` moved by `delta` (−1 up, +1 down), as a new array.
 * A move off either end is not an error — the button for it is disabled —
 * so it simply returns the list unchanged.
 */
export function moved(items, index, delta) {
  const target = index + delta
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) return items
  const next = [...items]
  const [item] = next.splice(index, 1)
  next.splice(target, 0, item)
  return next
}

/**
 * The `assignLesson` calls that make a stage's stored order match `lessons`
 * (already in the order the admin wants), as `[{ lessonId, orderInStage }]`.
 *
 * `orderInStage` is not unique on the backend — ties fall back to the flat
 * `displayOrder`, and a null sorts after every numbered lesson — so moving
 * one lesson by writing one position is not enough to pin an order down.
 * Renumbering 0..n-1 is, and only the rows that are not already at their
 * index are sent, so the usual swap costs two calls, not n.
 */
export function renumberPlan(lessons) {
  return lessons
    .map((lesson, index) => ({ lessonId: lesson.id, orderInStage: index }))
    .filter((plan, index) => lessons[index].orderInStage !== index)
}

/**
 * The published lessons of `unassignedLessons` — the ones the warning is
 * about. A draft or archived lesson in no stage is simply not ready yet;
 * a *published* one is live in the flat catalogue and absent from the
 * learner's path, which is invisible unless the panel says so.
 */
export function publishedWithoutStage(unassignedLessons) {
  return (unassignedLessons ?? []).filter(lesson => lesson.status === 'published')
}

/**
 * The refusals of `admin-arcade_*` in the admin's own language, or null when
 * this is not one of them — the caller then falls back to `errorMessage`,
 * which prefers whatever the server said.
 */
export function stageRefusal(error) {
  const code = error?.response?.data?.error
  if (code === 'stage_order_taken') return 'این جایگاه روی مسیر این سطح پر است؛ مرحله را بسازید و با کلیدهای بالا/پایین جابه‌جا کنید.'
  if (code === 'stage_set_mismatch') return 'ترتیب ارسالی با مرحله‌های این سطح نمی‌خواند؛ فهرست را تازه کنید و دوباره جابه‌جا کنید.'
  if (code === 'level_mismatch') return 'یک مرحله فقط درس‌های سطح خودش را می‌گیرد.'
  if (code === 'not_found') return 'این مرحله یا درس دیگر وجود ندارد؛ فهرست را تازه کنید.'
  return null
}

/** The Arcade's runtime settings (X5), in the order the read-only card lists them. */
export const ARCADE_SETTING_KEYS = [
  'arcade.starThresholds',
  'arcade.lessonUnlock',
  'arcade.stageUnlock',
  'arcade.enforceLockOnSubmit',
  'arcade.requirePremium',
]

/** What each of them is, for an admin who is looking at the path and not at the registry. */
export const ARCADE_SETTING_FA = {
  'arcade.starThresholds': 'آستانهٔ ستاره‌ها',
  'arcade.lessonUnlock': 'باز شدن درس در یک مرحله',
  'arcade.stageUnlock': 'باز شدن مرحلهٔ بعدی',
  'arcade.enforceLockOnSubmit': 'اعمال قفل هنگام ارسال ضبط',
  'arcade.requirePremium': 'مسیر فقط برای اشتراک ویژه',
}

/**
 * One Arcade setting's current value as a sentence, so the card can be read
 * without knowing the wire format. Anything unexpected (a key gaining a new
 * option before this file hears about it) falls back to the raw value rather
 * than to silence.
 */
export function describeArcadeSetting(key, value) {
  if (key === 'arcade.starThresholds') {
    if (!Array.isArray(value)) return String(value)
    return value.map((threshold, index) => `${faDigits(index + 1)}★ از ${faDigits(threshold)}`).join(' · ')
  }
  if (key === 'arcade.lessonUnlock') {
    if (value === 'previous_completed') return 'درس قبلیِ همان مرحله باید کامل شده باشد'
    if (value === 'none') return 'همهٔ درس‌های مرحلهٔ باز، باز هستند'
  }
  if (key === 'arcade.stageUnlock') {
    if (value === 'all_lessons_completed') return 'همهٔ درس‌های مرحلهٔ قبل باید کامل شده باشند'
    if (value === 'none') return 'همهٔ مرحله‌های منتشرشده باز هستند'
    if (typeof value === 'string' && value.startsWith('min_stars:')) {
      return `دست‌کم ${faDigits(value.slice('min_stars:'.length))} ستاره در مرحلهٔ قبل`
    }
  }
  if (typeof value === 'boolean') return value ? 'روشن' : 'خاموش'
  return typeof value === 'string' ? value : JSON.stringify(value)
}
